import { getSiteRoot } from './getSiteRoot';
import { Data } from '@generated/Foundation.GMI.model';
import { pickBy } from 'lodash';
import { getGraphQLClientAsync } from 'lib/graphql-client-factory/manual-config';

type GeneratedSiteSettings = NonNullable<Data.Settings.SiteSettings['fields']>;

export type SiteSettings = Omit<GeneratedSiteSettings, 'badgeSettings' | 'buyNowSettings'> & {
  badgeSettings?: Data.Settings.BadgeSettings | Data.Settings.BadgeSettings_Item;
  buyNowSettings?: GeneratedSiteSettings['buyNowSettings'] | Data.Settings.BuyNowSettings_Item;
};

export interface SiteRoot {
  id: string;
  name: string;
  path: string;
}

export async function getSiteSettings(
  siteName: string,
  language: string = 'en'
): Promise<SiteSettings | null> {
  const graphqlClient = await getGraphQLClientAsync();

  const siteRoot = await getSiteRoot(siteName, language);
  const siteRootId = siteRoot?.id;
  if (!siteRootId) {
    console.warn('Site root ID not found, skipping site settings fetch');
    return null;
  }

  // Second query to get site settings
  const settingsResult = await graphqlClient.request<GetSiteSettingsType>(SITE_SETTINGS_QUERY, {
    siteRootId,
    language,
  });

  // Transform the fields array into an object with field names as keys
  const siteSettingsResult = settingsResult.search?.results?.[0];

  const siteSettings = siteSettingsResult?.fields
    ? (Object.fromEntries(
        siteSettingsResult.fields.map((field) => [field.name, field.jsonValue])
      ) as SiteSettings)
    : undefined;

  const buyNowSettingsRaw = (siteSettings as Record<string, unknown> | undefined)?.[
    'buyNowSettings'
  ];

  if (siteSettings && buyNowSettingsRaw === null) {
    const buyNowSettingsPath = `${siteRoot.path}/Settings/Site Settings/Buy Now Settings`;
    const buyNowSettingsResult = await graphqlClient.request<GetBuyNowSettingsType>(
      BUY_NOW_SETTINGS_QUERY,
      {
        path: buyNowSettingsPath,
        language,
      }
    );

    if (buyNowSettingsResult.item) {
      siteSettings.buyNowSettings = {
        fields: buyNowSettingsResult.item.fields.reduce(
          (fields, field) => {
            fields[field.name] = field.jsonValue;
            return fields;
          },
          {} as Record<string, unknown>
        ),
      } as Data.Settings.BuyNowSettings_Item;
    }
  }

  // Store transformed settings in context
  return pickBy(
    siteSettings,
    // Undefined can't be JSON serialized, so we filter them out
    (value) => value !== undefined
  );
}

type GetSiteSettingsType = {
  search?: {
    results?: {
      fields: {
        name: keyof SiteSettings;
        jsonValue: SiteSettings[keyof SiteSettings];
      }[];
    }[];
  };
};

type GetBuyNowSettingsType = {
  item?: {
    fields: {
      name: string;
      jsonValue: unknown;
    }[];
  };
};

const SITE_SETTINGS_QUERY = `
  query GetSiteSettings($siteRootId: String!, $language: String!) {
    search(
      where: {
        AND: [
          { name: "_templates", value: "${Data.Settings.Constants.SiteSettings.TemplateId}" }
          { name: "_language", value: $language }
          { name: "_path", value: $siteRootId }
        ]
      }
    ) {
      results {
        fields {
          name
          jsonValue
        }
      }
    }
  }
`;

const BUY_NOW_SETTINGS_QUERY = `
  query GetBuyNowSettings($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      fields {
        name
        jsonValue
      }
    }
  }
`;
